import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { filterBy } from "@progress/kendo-data-query";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { icon } from "../../../../../../../config/constants";
import { VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "../../../../../WorkflowVariables/workflowVariables.constants";
import { WF_EXPRESSION_VARIABLE_GROUP_HEADERS, WF_EXPRESSION_VARIABLE_GROUP_NAMES } from "../../../../constants/expressionBuilder.constants";
import "../../../../styles/ExpressionBuilder.css";
import OptionsPopup from "./OptionsPopup/OptionsPopup";

/** Synthetic collapsed-group row id prefix (ComboBox only; ids are not real variables). */
const COMBO_BOX_COLLAPSED_ROW_ID_PREFIX = "__combobox_value_collapsed__";
/** Flag on placeholder rows so they cannot be selected. */
const COMBO_BOX_GROUP_COLLAPSED_FLAG = "__comboBoxInputGroupCollapsed";
/** Popup list height (px) so grouped lists scroll and sticky headers can track. */
const COMBO_BOX_GROUPED_LIST_POPUP_HEIGHT_PX = 320;

/**
 * Builds flat list data with collapsed groups replaced by one placeholder row per group.
 * @param {Array<Object>} fields - Field rows
 * @param {Set<string>} collapsedGroupKeySet - Raw `group` values that are collapsed
 * @returns {Array<Object>}
 * @author Abhishek Dhillon
 */
const buildComboBoxFlatListData = (fields, collapsedGroupKeySet) => {
  if (!fields?.length) return fields || [];
  if (!collapsedGroupKeySet?.size) return [...fields];

  const groupOrder = [];
  const seen = new Set();
  for (const f of fields) {
    const g = f.group ?? "";
    if (!seen.has(g)) {
      seen.add(g);
      groupOrder.push(g);
    }
  }

  const out = [];
  for (const g of groupOrder) {
    const groupItems = fields.filter((f) => (f.group ?? "") === g);
    if (collapsedGroupKeySet.has(g)) {
      out.push({
        id: `${COMBO_BOX_COLLAPSED_ROW_ID_PREFIX}${g}`,
        name: "\u00a0",
        group: g,
        [COMBO_BOX_GROUP_COLLAPSED_FLAG]: true,
      });
    } else {
      out.push(...groupItems);
    }
  }
  return out;
};

/**
 * Slug for stable header toggle ids.
 * @param {string} groupKey - Raw group key
 * @returns {string}
 * @author Abhishek Dhillon
 */
const getComboBoxGroupToggleIdFragment = (groupKey) =>
  String(groupKey ?? "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .toLowerCase() || "group";

/**
 * Resolves group key from Kendo header APIs (`group` on second arg) with fallback to default child text.
 * @param {React.ReactElement} element - List or sticky wrapper element
 * @param {{ group?: string }|undefined} kendoGroupProps - From `groupHeaderItemRender` / `groupStickyHeaderItemRender`
 * @returns {string}
 * @author Abhishek Dhillon
 */
const resolveComboBoxGroupHeaderKey = (element, kendoGroupProps) => {
  const fromKendo = kendoGroupProps?.group;
  if (fromKendo != null && String(fromKendo) !== "") {
    return String(fromKendo);
  }
  const children = element.props.children;
  if (children?.props?.children != null && String(children.props.children) !== "") {
    return String(children.props.children);
  }
  return "";
};

/**
 * Guarantees `name` (textField) and `id` (valueField) so Kendo ListItem never does `undefined.toString()`.
 * ListItem renders default text before `itemRender`, so this must be fixed on the data, not in itemRender.
 * @param {Array<Object>} items - Rows bound to the ComboBox
 * @returns {Array<Object>}
 * @author Abhishek Dhillon
 */
const sanitizeComboBoxListItems = (items) => {
  if (!items?.length) return [];
  return items.map((row, index) => {
    if (!row) {
      return {
        id: `__combo_empty_row_${index}`,
        name: "\u00a0",
        group: "",
      };
    }
    if (row[COMBO_BOX_GROUP_COLLAPSED_FLAG]) {
      return {
        ...row,
        group: row.group ?? "",
        name: "\u00a0",
        id: row.id != null ? row.id : `${COMBO_BOX_COLLAPSED_ROW_ID_PREFIX}${index}`,
      };
    }
    const nameStr = row.name == null || row.name === "" ? "\u00a0" : typeof row.name === "string" ? row.name : String(row.name);
    const idVal = row.id != null && row.id !== "" ? row.id : `__combo_fallback_id_${index}`;
    return { ...row, name: nameStr, id: idVal };
  });
};

/**
 * ComboBox input component for dropdown and text fields with hover popup
 * @param {string} value - Current value
 * @param {Array} availableFields - Available field options
 * @param {Function} onValueChange - Value change handler
 * @param {Object} selectedField - Selected field object with options
 * @returns {JSX.Element} ComboBox input component with popup
 * @author Abhishek Dhillon
 * @parent ValueInput
 */
const ComboBoxInput = ({ value, availableFields, onValueChange, selectedField }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const comboRef = useRef(null);
  const containerRef = useRef(null);
  const [comboObj, setComboObj] = useState({
    open: false,
  });
  const [filteredFields, setFilteredFields] = useState(availableFields ?? []);
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState([]);

  const collapsedGroupKeySet = useMemo(() => new Set(collapsedGroupKeys), [collapsedGroupKeys]);

  const sanitizedFilteredFields = useMemo(() => sanitizeComboBoxListItems(filteredFields), [filteredFields]);

  const comboBoxListData = useMemo(() => {
    const built = buildComboBoxFlatListData(sanitizedFilteredFields, collapsedGroupKeySet);
    return sanitizeComboBoxListItems(built);
  }, [sanitizedFilteredFields, collapsedGroupKeySet]);

  useEffect(() => {
    setFilteredFields(availableFields ?? []);
  }, [availableFields]);

  useEffect(() => {
    const present = new Set(sanitizedFilteredFields.map((f) => f.group ?? ""));
    setCollapsedGroupKeys((prev) => {
      const next = prev.filter((k) => present.has(k));
      return next.length === prev.length && next.every((k, i) => k === prev[i]) ? prev : next;
    });
  }, [sanitizedFilteredFields]);

  /**
   * Toggles expanded/collapsed rows for one variable group
   * @param {string} groupKey - Raw `group` value
   * @author Abhishek Dhillon
   */
  const toggleGroupCollapse = useCallback((groupKey) => {
    if (groupKey == null || groupKey === "") return;
    const k = String(groupKey);
    setCollapsedGroupKeys((prev) => {
      const idx = prev.indexOf(k);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, k];
    });
  }, []);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
    setHoveredItem(null);
    setComboObj((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Handles ComboBox value change
   * @param {Object} event - ComboBox change event
   * @author Abhishek Dhillon
   */
  const handleComboBoxChange = useCallback(
    (event) => {
      const raw = event?.value ?? event?.target?.value;
      if (raw?.[COMBO_BOX_GROUP_COLLAPSED_FLAG]) return;
      onValueChange(
        raw
          ? {
              custom: true,
              ...raw,
            }
          : raw
      );
    },
    [onValueChange]
  );

  /**
   * Handles click on ComboBox item with options
   * @param {Object} event - Click event
   * @param {Object} dataItem - Data item being clicked
   * @author Abhishek Dhillon
   */
  const handleItemClick = useCallback(
    (event, dataItem) => {
      if (dataItem?.[COMBO_BOX_GROUP_COLLAPSED_FLAG]) {
        event?.preventDefault();
        return;
      }
      if (dataItem?.options?.length > 0) {
        event?.preventDefault(); // Prevent ComboBox from selecting the item
        setHoveredItem(dataItem);
        setComboObj((prev) => ({ ...prev, open: false }));
        setShowPopup(true);
      } else {
        event?.preventDefault();
        onValueChange({
          name: dataItem?.name,
          value: dataItem?.name,
          custom: false,
          group: dataItem?.group,
          ref: {
            id: dataItem?.id,
            variableId: dataItem?.variableId,
            fixedVariableId: dataItem?.fixedVariableId,
          },
        });
        handleClosePopup();
      }
    },
    [onValueChange]
  );

  /**
   * Handles clicking on an option in the popup
   * @param {string} optionValue - Selected option value
   * @author Abhishek Dhillon
   */
  const handleOptionClick = useCallback(
    (e, optionValue) => {
      e.preventDefault();
      onValueChange(optionValue);
      handleClosePopup();
    },
    [onValueChange, handleClosePopup]
  );

  /**
   * Handles filter change on the ComboBox list
   * @param {Object} e - Kendo filter event
   * @author Abhishek Dhillon
   */
  const handleFilterChange = useCallback(
    (e) => {
      setFilteredFields(filterBy(availableFields ?? [], e?.filter));
    },
    [availableFields]
  );

  /**
   * Custom item render function for ComboBox options with click functionality
   * @param {Object} li - List item element
   * @param {Object} itemProps - Item props from ComboBox
   * @returns {JSX.Element} Rendered list item with click handler
   * @author Abhishek Dhillon
   */
  const itemRender = useCallback(
    (li, itemProps) => {
      const dataItem = itemProps?.dataItem;
      if (dataItem?.[COMBO_BOX_GROUP_COLLAPSED_FLAG]) {
        return null;
      }
      return React.cloneElement(
        li,
        {
          ...li?.props,
          onClick: (e) => handleItemClick(e, dataItem),
          style: {
            ...li?.props?.style,
            cursor: dataItem?.options?.length > 0 ? "pointer" : "default",
            position: "relative",
          },
        },
        <div className='combobox-item-content'>
          <span>
            {VARIABLE_ICONS[dataItem?.type]}
            {li?.props?.children}
          </span>
          {itemProps?.dataItem?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD && (
            <div title='Global' className={`custom-variable smart-chip-lock mr-2`}>
              {icon.LOCK_ICON}
            </div>
          )}
          {dataItem?.options?.length > 0 && <small className='text-muted ms-2'>({dataItem?.options?.length} options)</small>}
        </div>
      );
    },
    [handleItemClick]
  );

  const getHeaderText = (headerValue) => {
    if (!headerValue) return headerValue;
    if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.FORM_SMARTCHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.FORM_SMARTCHIPS;
    } else if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.CREATOR_CHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.CREATOR_CHIPS;
    } else if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.ASSIGNEE_CHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.ASSIGNEE_CHIPS;
    } else if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.USER_CHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.USER_CHIPS;
    }
    return headerValue;
  };

  /**
   * Renders group header (list or sticky) with expand/collapse
   * @param {React.ReactElement} el - Kendo element
   * @param {"list"|"sticky"} variant - Id suffix
   * @param {{ group?: string }|undefined} kendoGroupProps - Kendo second argument
   * @returns {React.ReactElement}
   * @author Abhishek Dhillon
   */
  const renderGroupHeaderControl = (el, variant, kendoGroupProps) => {
    const groupKey = resolveComboBoxGroupHeaderKey(el, kendoGroupProps);
    const headerText = getHeaderText(groupKey);
    const isCollapsed = collapsedGroupKeySet.has(groupKey);
    const toggleId = `combobox-value-group-toggle-${variant}-${getComboBoxGroupToggleIdFragment(groupKey)}`;

    const stopListNavigation = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const onHeaderActivate = (e) => {
      stopListNavigation(e);
      toggleGroupCollapse(groupKey);
    };

    const onHeaderKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        stopListNavigation(e);
        toggleGroupCollapse(groupKey);
      }
    };

    const itemHeader = (
      <span
        id={toggleId}
        role='button'
        tabIndex={0}
        aria-expanded={!isCollapsed}
        className='d-flex align-items-center gap-2 cursor-pointer w-100'
        onClick={onHeaderActivate}
        onKeyDown={onHeaderKeyDown}>
        <span>{isCollapsed ? icon.ARROW_DOWN : icon.ARROW_UP}</span>
        {headerText}
      </span>
    );
    return React.cloneElement(el, { ...el.props, "data-expression-group-header": groupKey }, itemHeader);
  };

  const groupHeaderItemRender = (li, itemProps) => renderGroupHeaderControl(li, "list", itemProps);

  const stickyHeaderRender = (div, stickyHeaderProps) => renderGroupHeaderControl(div, "sticky", stickyHeaderProps);

  return (
    <>
      <div className='combobox-input-container' ref={containerRef} onClick={() => setComboObj((prev) => ({ ...prev, open: true }))}>
        <ComboBox
          ref={comboRef}
          textField='name'
          valueField='id'
          groupField='group'
          data={comboBoxListData}
          value={value}
          onChange={handleComboBoxChange}
          required={true}
          id='value-input-combobox'
          className='expression-builder-w-100'
          allowCustom={true}
          fillMode='solid'
          filterable={true}
          onFilterChange={handleFilterChange}
          itemRender={itemRender}
          adaptive={true}
          clearButton={true}
          suggest={true}
          placeholder='Enter or select value'
          groupHeaderItemRender={groupHeaderItemRender}
          groupStickyHeaderItemRender={stickyHeaderRender}
          opened={comboObj.open}
          onOpen={() => setShowPopup(false)}
          onBlur={() => setComboObj({ open: false })}
          onClose={() => setComboObj({ open: false })}
          onFocus={() => setComboObj({ open: true })}
        />
      </div>
      <OptionsPopup
        showPopup={showPopup}
        setComboObj={setComboObj}
        popupAnchor={containerRef}
        hoveredItem={hoveredItem}
        onOptionClick={handleOptionClick}
        onClose={handleClosePopup}
      />
    </>
  );
};

export default ComboBoxInput;
